/**
 * CalibrationFactorsCard - Component for displaying calibration factors and apply button
 */

import './RigCalibration.css';
import { CalibrationFactorsCardProps } from './types';
import { getNumericValue } from './utils';

import { Card, Text, Title, Stack, Group, Button, Divider } from '@mantine/core';

import IconDeviceFloppy from '@tabler/icons-react/IconDeviceFloppy';
import { useLanguage } from '@/i18n/LanguageContext';
import { formatFixedNumber } from '@/utils/numberFormat';

interface CalculationDisplayProps {
  kind: 'oil' | 'water';
  predicted: number | null;
  retort: number | null;
  isExample?: boolean;
}

function CalculationDisplay({ kind, predicted, retort, isExample = false }: CalculationDisplayProps) {
  const { t, language } = useLanguage();
  const label = kind === 'oil' ? t('calibration.oilFactor') : t('calibration.waterFactor');
  const kindLabel = kind === 'oil' ? t('calibration.oilPercent') : t('calibration.waterPercent');
  const predictedColor = isExample ? (predicted !== null ? 'blue' : 'dimmed') : 'blue';
  const retortColor = isExample ? (retort !== null ? 'orange' : 'dimmed') : 'orange';
  
  const hasBothValues = predicted !== null && retort !== null;
  const hasNoValues = predicted === null && retort === null;
  const factor = hasBothValues ? predicted - retort : null;
  const factorColor = isExample ? (hasBothValues ? 'dark' : 'dimmed') : 'dark';

  if (isExample && hasNoValues) {
    return (
      <Stack gap="xs">
        <Text size="sm" fw={700} c="dimmed">
          {label}:
        </Text>
        <Text size="md" c="dimmed" fs="italic">
          {t('calibration.selectRetortAndResult')}
        </Text>
      </Stack>
    );
  }

  const predictedDisplay = predicted !== null 
    ? formatFixedNumber(predicted, language, 2)
    : language === 'es' ? '??,??' : '??.??';
  const retortDisplay = retort !== null 
    ? formatFixedNumber(retort, language, 2)
    : language === 'es' ? '??,??' : '??.??';
  const factorDisplay = factor !== null
    ? formatFixedNumber(factor, language, 2)
    : language === 'es' ? '??,??' : '??.??';

  return (
    <Stack gap="xs">
      <Text size="sm" fw={700} c="dimmed">
        {label}:
      </Text>
      <Group gap="xs" align="center" wrap="wrap">
        <Text size="lg" fw={500} c={predictedColor}>
          {predictedDisplay}
        </Text>
        <Text size="lg" c="dimmed">-</Text>
        <Text size="lg" fw={500} c={retortColor}>
          {retortDisplay}
        </Text>
        <Text size="lg" c="dimmed">=</Text>
        <Text size="lg" fw={700} c={factorColor}>
          {factorDisplay}
        </Text>
      </Group>
      <Text size="sm" c="dimmed">
        {t('calibration.formula', { kind: kindLabel.replace('%', '').trim() })}
      </Text>
    </Stack>
  );
}

export default function CalibrationFactorsCard({
  selectedTestResult,
  selectedRetortHistory,
  density,
  oilPercent,
  waterPercent,
  saving,
  onApply,
}: CalibrationFactorsCardProps) {
  const { t } = useLanguage();
  // Check if we have the required data to show calibration factors
  const hasRetortData = selectedRetortHistory || (density !== '' && oilPercent !== '' && waterPercent !== '');
  const canCalculate = selectedTestResult && hasRetortData;

  // Get all values for calculation display
  const getCalculationValues = () => {
    if (!selectedTestResult) return null;

    const predictedOil = selectedTestResult.base_oil_vol_per_predicted_before_calibration ?? 0;
    const predictedWater = selectedTestResult.water_vol_per_predicted_before_calibration ?? 0;

    const retortOil = selectedRetortHistory
      ? selectedRetortHistory.oil_percentage
      : getNumericValue(oilPercent);
    const retortWater = selectedRetortHistory
      ? selectedRetortHistory.water_percentage
      : getNumericValue(waterPercent);

    const oilFactor = predictedOil - retortOil;
    const waterFactor = predictedWater - retortWater;

    return {
      predictedOil,
      predictedWater,
      retortOil,
      retortWater,
      oilFactor,
      waterFactor,
    };
  };

  const targetSlot = selectedRetortHistory?.sample_source ?? null;
  const values = canCalculate ? getCalculationValues() : null;

  // Get partial values for example preview
  const getPartialValues = () => {
    const retortOil = selectedRetortHistory
      ? selectedRetortHistory.oil_percentage
      : (typeof oilPercent === 'number' ? oilPercent : null);
    const retortWater = selectedRetortHistory
      ? selectedRetortHistory.water_percentage
      : (typeof waterPercent === 'number' ? waterPercent : null);

    const predictedOil = selectedTestResult?.base_oil_vol_per_predicted_before_calibration ?? null;
    const predictedWater = selectedTestResult?.water_vol_per_predicted_before_calibration ?? null;

    return { retortOil, retortWater, predictedOil, predictedWater };
  };

  const partialValues = getPartialValues();

  // Render example preview
  const renderExamplePreview = () => (
    <Stack gap="md">
      <CalculationDisplay
        kind="oil"
        predicted={partialValues.predictedOil}
        retort={partialValues.retortOil}
        isExample
      />

      <Divider />

      <CalculationDisplay
        kind="water"
        predicted={partialValues.predictedWater}
        retort={partialValues.retortWater}
        isExample
      />

      <Divider />

      <Group justify="space-between" align="center">
        <Group gap="md">
          <Text size="sm" fw={700} c="dimmed">{t('calibration.target')}</Text>
          {targetSlot ? (
            <Text size="sm" fw={700}>
              {targetSlot}
            </Text>
          ) : (
            <Text size="sm" c="dimmed" fs="italic">
              {t('calibration.selectRetortSource')}
            </Text>
          )}
        </Group>
        <Button
          leftSection={<IconDeviceFloppy size={18} />}
          disabled
          className="rc-button"
          size="md"
        >
          {t('calibration.apply')}
        </Button>
      </Group>
    </Stack>
  );

  // Render actual calculation
  const renderActualCalculation = () => {
    if (!values || !targetSlot) return null;

    return (
      <Stack gap="md">
        <CalculationDisplay
          kind="oil"
          predicted={values.predictedOil}
          retort={values.retortOil}
        />

        <Divider />

        <CalculationDisplay
          kind="water"
          predicted={values.predictedWater}
          retort={values.retortWater}
        />

        <Divider />

        <Group justify="space-between" align="center">
          <Group gap="md">
            <Text size="sm" fw={700}>{t('calibration.target')}</Text>
            <Text size="sm" fw={700}>
              {targetSlot}
            </Text>
          </Group>
          <Button
            onClick={onApply}
            leftSection={<IconDeviceFloppy size={18} />}
            loading={saving}
            disabled={saving}
            className="rc-button"
            size="md"
          >
            {t('calibration.apply')}
          </Button>
        </Group>
      </Stack>
    );
  };

  return (
    <Card withBorder padding="md" radius="md" className="rc-calibrationCard rc-card">
      <Title order={2} className="rc-sectionTitle rc-sectionTitle--light" style={{ marginBottom: 4 }}>
        {t('calibration.applyStep')}
      </Title>
      <Divider style={{ marginBottom: 8 }} />
      {canCalculate ? renderActualCalculation() : renderExamplePreview()}
    </Card>
  );
}
